
export interface WorkflowTemplate {
  id: string
  title: string
  description: string
  icon: string
  prompt: string
  category: string
}

const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  // Verified simple templates that work with MobiFin
  {
    id: 'simple-payment',
    title: 'Simple Payment',
    description: 'Basic payment acceptance workflow - verified to work with MobiFin',
    icon: '✅',
    category: 'Verified',
    prompt: 'Make a workflow to accept a customer payment.'
  },
  {
    id: 'payment-with-auth',
    title: 'Payment with Authentication',
    description: 'Payment with customer authentication - verified to work with MobiFin',
    icon: '✅',
    category: 'Verified',
    prompt: 'Make a workflow to accept a customer payment. First authenticate the customer, then process the payment.'
  },
  {
    id: 'payment-with-balance',
    title: 'Payment with Balance Check',
    description: 'Payment with balance validation - verified to work with MobiFin',
    icon: '✅',
    category: 'Verified',
    prompt: 'Make a workflow to accept a customer payment. Check if customer has sufficient balance. If yes, process payment. If no, reject with insufficient funds message.'
  },
  {
    id: 'payment-auth-validation',
    title: 'Payment with Auth & Validation',
    description: 'Payment with authentication and balance check - verified to work with MobiFin',
    icon: '✅',
    category: 'Verified',
    prompt: 'Make a workflow to accept payment. Authenticate customer, check balance. If sufficient funds, process payment. If insufficient funds, reject with error message.'
  },
  
  /* Commented out complex templates until PREPAREANDSENDNOTIFICATION is sorted
  {
    id: 'payment',
    title: 'Payment Processing',
    description: 'Handle payment transactions with balance checks and notifications',
    icon: '💳',
    category: 'Financial',
    prompt: `I need to create a payment processing workflow. Here's what should happen:

1. Customer initiates payment with amount
2. Customer's balance is checked  
3. If balance is insufficient, decline and notify customer
4. If balance is sufficient, proceed with payment
5. Debit customer account
6. Credit merchant account
7. Apply any applicable transaction fees
8. Generate transaction receipt
9. Send confirmation to both parties
10. Log transaction for audit

Make sure to include proper error handling and notification steps.`
  },
  {
    id: 'loan',
    title: 'Loan Application',
    description: 'Complete loan application process with credit checks and approvals',
    icon: '🏦',
    category: 'Financial',
    prompt: `This workflow handles the complete customer loan application process from initial submission to final approval or rejection.

Process Flow:
1. Start Event: Customer submits loan application through web portal
2. Data Validation Task: Validate all required fields are present and properly formatted
3. Credit Check Gateway: Perform credit score verification using external credit bureau API
4. Risk Assessment Decision:
   - If credit score < 600: Route to automatic rejection
   - If credit score 600-750: Route to manual review
   - If credit score > 750: Route to automatic pre-approval
5. Automatic Rejection Path:
   - Send rejection notification email to customer
   - Log rejection reason in audit system
   - End process
6. Manual Review Path:
   - Assign to loan officer task queue
   - Loan officer reviews application details
   - Officer makes approval/rejection decision
   - If approved, set loan terms (interest rate, repayment schedule)
   - Continue to document verification
7. Pre-Approval Path:
   - Generate pre-approval letter
   - Set preliminary loan terms
   - Continue to document verification
8. Document Verification Gateway:
   - Request proof of income documents
   - Request proof of identity documents
   - Verify employment status
   - Validate collateral if applicable
9. Final Approval Decision:
   - If all documents verified: Proceed to loan disbursement
   - If documents insufficient: Request additional information
   - If documents invalid: Reject application
10. Loan Disbursement Process:
    - Create loan account in core banking system
    - Generate loan agreement documents
    - Schedule disbursement transaction
    - Set up automatic repayment schedule
11. Customer Notification:
    - Send approval notification with loan details
    - Provide access to loan agreement
    - Share repayment schedule
12. End Event: Process complete

The workflow should handle exceptions, timeouts for document submission, and maintain a complete audit trail.`
  },
  {
    id: 'kyc',
    title: 'KYC Verification',
    description: 'Customer onboarding with identity verification and compliance checks',
    icon: '👤',
    category: 'Compliance',
    prompt: `Create a complete KYC (Know Your Customer) verification workflow for new customer onboarding.

Requirements:
1. Customer Registration Start Event
2. Basic Information Collection:
   - Full name, date of birth, address
   - Contact information (email, phone)
   - Tax identification number
3. Identity Document Upload Gateway:
   - Accept passport, driver's license, or national ID
   - Validate document format and quality
4. Liveness Check Process:
   - Capture selfie photo
   - Perform facial recognition match with ID document
   - Verify liveness (not a static image)
5. Address Verification:
   - Request proof of address document (utility bill, bank statement)
   - Validate document is recent (within 3 months)
   - Cross-reference address with provided information
6. Sanctions Screening Gateway:
   - Check against global sanctions lists
   - Screen for PEP (Politically Exposed Person) status
   - Review adverse media reports
7. Risk Scoring Decision:
   - Calculate risk score based on all factors
   - Low Risk: Automatic approval
   - Medium Risk: Enhanced due diligence required
   - High Risk: Senior management approval needed
8. Enhanced Due Diligence (if required):
   - Source of funds verification
   - Employment verification
   - Additional reference checks
9. Compliance Review:
   - Final compliance officer review for medium/high risk
   - Document any special conditions
10. Account Activation:
    - Create customer profile
    - Set account permissions based on risk level
    - Generate welcome package
11. Customer Communication:
    - Send approval/rejection notification
    - Provide account access credentials if approved
    - Share rejection reason and appeal process if declined
12. Audit and Reporting:
    - Log all verification steps
    - Store documents securely
    - Generate compliance report

Include proper error handling, timeout management for document submissions, and maintain regulatory compliance throughout.`
  }
  */
]

interface TemplateSelectorProps {
  onSelectTemplate: (template: WorkflowTemplate) => void
  className?: string
}

export default function TemplateSelector({ onSelectTemplate, className = '' }: TemplateSelectorProps) {
  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Quick Start Templates</h2>
        <p className="text-sm text-gray-600">
          Select a pre-defined workflow template to quickly test the conversational BPMN builder
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {WORKFLOW_TEMPLATES.map((template) => (
          <button
            key={template.id}
            onClick={() => onSelectTemplate(template)}
            className="group relative flex flex-col items-start p-4 bg-gray-50 rounded-lg hover:bg-blue-50 transition-all duration-200 hover:shadow-md text-left"
          >
            <div className="flex items-center w-full mb-3">
              <span className="text-2xl mr-3">{template.icon}</span>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 group-hover:text-blue-500">
                  {template.title}
                </h3>
                <span className="text-xs text-gray-500 mt-1">{template.category}</span>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 line-clamp-2">
              {template.description}
            </p>
            
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">How it works:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Click a template to load it into the chat</li>
              <li>Press Send or customize the prompt</li>
              <li>Watch the BPMN diagram build in real-time</li>
              <li>Continue the conversation to refine the workflow</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )
}

export { WORKFLOW_TEMPLATES }